import { TFunction } from 'next-i18next';
import { LOCALE_SEARCH_PATHS } from 'types/locales';
import { formatI18nCompactPrice, getCurrencyFromLocale } from './i18n-format-currency';

/**
 * Formats a price range using compact notation (e.g., 1.5M, 500K).
 * Uses i18n formatting for proper locale support.
 * 
 * @param min - Minimum price value
 * @param max - Maximum price value
 * @param t - Translation function from next-i18next
 * @param locale - The locale to use for formatting (defaults to 'en-CA')
 * @returns Formatted price range string
 * 
 * @example
 * formatPriceRange(500000, 1000000, t, 'en-CA') // "500K - 1M"
 * formatPriceRange(500000, null, t, 'fr-CA') // "Above 500K"
 */
export default function formatPriceRange(
  min: number | string | null, 
  max: number | string | null, 
  t?: TFunction,
  locale: string = 'en-CA'
) {
  const currency = getCurrencyFromLocale(locale);
  
  const formatValue = (value: number | string) => {
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    return formatI18nCompactPrice(numValue, locale, currency);
  };

  if (min && max) {
    return t(LOCALE_SEARCH_PATHS.FILTER_LABELS_PRICE_RANGE, { 
      defaultValue: '{{min}} - {{max}}',
      min: formatValue(min), 
      max: formatValue(max), 
    });
  } else if (min && !max) {
    return t(LOCALE_SEARCH_PATHS.FILTER_LABELS_ABOVE_PRICE, { 
      defaultValue: 'Above {{price}}',
      price: formatValue(min), 
    });
  } else if (!min && max) {
    return t(LOCALE_SEARCH_PATHS.FILTER_LABELS_BELOW_PRICE, { 
      defaultValue: 'Below {{price}}',
      price: formatValue(max), 
    });
  } else {
    return t ? t(LOCALE_SEARCH_PATHS.FILTER_LABELS_ANY_PRICE, { defaultValue: 'Any Price' }) : 'Any Price';
  }
}

/**
 * Formats a square footage range.
 * 
 * @param min - Minimum square footage value
 * @param max - Maximum square footage value
 * @param t - Translation function from next-i18next
 * @returns Formatted square footage range string
 * 
 * @example
 * formatSqftRange(1000, 2000, t) // "1000 - 2000 Sqft"
 * formatSqftRange(1000, null, t) // "Above 1000 Sqft"
 */
export const formatSqftRange = (min: number | string | null, max: number | string | null, t?: TFunction) => {
  if (min && max) {
    return t(LOCALE_SEARCH_PATHS.FILTER_LABELS_SQFT_RANGE, { 
      defaultValue: '{{min}} - {{max}} Sqft',
      min: min, 
      max: max, 
    });
  } else if (min && !max) {
    return t(LOCALE_SEARCH_PATHS.FILTER_LABELS_ABOVE_SQFT, { 
      defaultValue: 'Above {{sqft}} Sqft',
      sqft: min, 
    });
  } else if (!min && max) {
    return t(LOCALE_SEARCH_PATHS.FILTER_LABELS_BELOW_SQFT, { 
      defaultValue: 'Below {{sqft}} Sqft',
      sqft: max, 
    });
  } else {
    return t ? t(LOCALE_SEARCH_PATHS.FILTER_LABELS_SQUARE_FEET, { defaultValue: 'Square Feet' }) : 'Square Feet';
  }
};
